import React, { useEffect, useState } from "react";

import { validate as uuidValidate } from "uuid";

import { getFieldAdmin } from "services";

import { getAdminPreviousOpenFields, setAdminPreviousOpenFields } from "utils";

import { COUNTRY_CODE } from "consts";

import { Coordinates, ZERO_UUID } from "models";

import { AdminTable, Button, Col, InputSearch, MainPage, Row, Typography, Message as message } from "components";

import { FieldDrawer } from "pages/admin/verifications/components/drawers/FieldDrawer";

type FarmAddress = {
  addressStreet: string;
  addressNumber: string;
  addressCity: string;
  zip: string;
  region: string;
  country: COUNTRY_CODE;
};

export type FieldAdmin = {
  id: string;
  farmId: string;
  name: string;
  number: string;
  flik: string;
  location: Coordinates | null;
  size: number;
  verificationSize: number;
  verifications: {
    id: string;
    verified: boolean | null;
    size: number;
    officialSize: number | null;
    verifiedByAdminUser: string;
    year: number;
  }[];
  farmAddress: FarmAddress;
  redzone: {
    geometry: Coordinates[];
    redzoneId: string;
    year: number;
    confirmed: boolean | null;
    accurate: boolean;
  } | null;
};

const mapVerificationsToString = (verifications: FieldAdmin["verifications"]) => {
  if (verifications.length === 0) return "not in verification yet";
  if (verifications.some(({ verified }) => verified === false)) return "rejected";
  if (verifications.some(({ verified }) => verified === null)) return "pending";
  return "approved";
};

export const QuickFieldAccess = () => {
  const [search, setSearch] = useState("");
  const [field, setField] = useState<FieldAdmin | null>(null);
  const [loading, setLoading] = useState(false);
  const [previousOpenFields, setPreviousOpenFields] = useState<FieldAdmin[]>([]);

  const updatePreviousOpenFields = (fields: FieldAdmin[]) => {
    setAdminPreviousOpenFields(fields);
    setPreviousOpenFields(fields);
  };

  const handleDataChange = () => {
    // Refetch data of field
    if (field) {
      fetchField(field.id);
    }
  };

  const openItem = (item: FieldAdmin) => {
    fetchField(item.id);
  };

  const fetchField = (id: string | null = null) => {
    const searchValue = id ?? search;

    // Valeria's Magical Mushroom™ Easter Egg - Empty Search
    if (!searchValue.trim()) {
      message({
        content: "Valeria's Legendary Mushroom™ sprouts! 🍄 +10 Verification Luck, +5 Field Insight",
        icon: "gremlin",
      });
      return;
    }

    const isValid = uuidValidate(searchValue);
    if (isValid) {
      setLoading(true);
      setSearch("");
      getFieldAdmin({
        id: searchValue,
        signal: new AbortController().signal,
      }).then((res) => {
        if (res?.fieldDetails) {
          const fields = previousOpenFields.filter((field) => field.id !== res.fieldDetails.id);
          setField(res.fieldDetails);
          updatePreviousOpenFields([res.fieldDetails, ...fields]);
          setLoading(false);
        } else {
          setLoading(false);
          message({
            content: "The gremlins didn'd find this field, feed them a field ID they can munch on... nom nom nom",
            icon: "gremlin",
          });
        }
      });
    } else {
      setLoading(false);
      message({
        content: "Uh-oh! The gremlins didn't fancy that ID... feed them a valid one!",
        icon: "gremlin",
      });
    }
  };

  // On mount we need clean up the fields in local storage which are from last year's verification
  useEffect(() => {
    const previousOpenFields = getAdminPreviousOpenFields();

    if (!previousOpenFields) {
      return;
    }

    // Reset previous fields in local storage and then only the ones again which still have a verification id
    updatePreviousOpenFields([]);

    Promise.all(
      previousOpenFields.map(async (prevField) => {
        const field = await getFieldAdmin({ id: prevField.id, signal: new AbortController().signal });

        if (field?.fieldDetails?.verifications?.length) {
          return field.fieldDetails;
        }
      })
    ).then((fields) => updatePreviousOpenFields(fields.filter(Boolean)));
  }, []);

  return (
    <>
      <MainPage pageName="admin">
        <Typography variant="h1">Quick Field Access</Typography>
        <Row className="margin-top-200">
          <Col sm={10} md={10}>
            <InputSearch value={search} isSmallVariant={true} label={`fieldId - ${ZERO_UUID}`} onChange={(e) => setSearch(e.target.value)} />
          </Col>
          <Col sm={2} md={2}>
            <Button type="primary" size="small" loading={loading} onClick={() => fetchField()}>
              Search
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            {/* @ts-expect-error: AdminTable requires props we don't use in this context */}
            <AdminTable
              dataSource={previousOpenFields.map((field) => ({ ...field, verified: mapVerificationsToString(field.verifications) }))}
              config={{
                exclude: [
                  "nearby",
                  "farmId",
                  "soilType",
                  "soilPoints",
                  "humusContent",
                  "soilDate",
                  "location",
                  "crops",
                  "flik",
                  "reviewed",
                  "verifications",
                  "totalPoints",
                  "exceedsHectareLimit",
                  "farmAddress",
                  "redzone",
                ],
                actions: [
                  {
                    icon: "tabler/eye",
                    func: openItem,
                    tooltip: "Re-open field drawer",
                  },
                ],
              }}
            />
          </Col>
        </Row>
      </MainPage>
      {!!field && (
        <FieldDrawer
          field={{
            ...field,
            size: field.verificationSize ?? field.size,
            disabledBySplit: false,
            editingRequests: {
              fieldLevel: { open: 0, toReview: 0, reviewed: 0 },
              cropLevel: { open: 0, toReview: 0, reviewed: 0 },
              fertilizerLevel: { open: 0, toReview: 0, reviewed: 0 },
              tillageSeedingLevel: { open: 0, toReview: 0, reviewed: 0 },
            },
            pendingPractices: {
              crops: 0,
              fertilizers: 0,
              tillageSeeding: 0,
            },
          }}
          farm={field?.farmAddress}
          farmId={field?.farmId}
          visible={!!field}
          loadingFields={loading}
          onClose={() => setField(null)}
          onDataChange={handleDataChange}
          payoutApproved={false}
        />
      )}
    </>
  );
};
